import { ORDER_FILLS_PAGE_SIZE } from "~/v3-poc/api/orderFills";
import type { OrderFill } from "~/v3-poc/api/orderFills.schema";
import type { OrdersWidgetBadgeColor } from "./OrdersWidget.types";
import {
  formatOrderLabel,
  formatOrderPrice,
  formatOrderQuantity,
  formatOrderTimestamp,
  getOrderSideBadgeColor,
  ORDERS_UNAVAILABLE_VALUE,
} from "./OrdersWidget.utils";

const EMPTY_FILLS: OrderFill[] = [];

export type OrderFillExecutionKind = "partial_fill" | "fill" | "unknown";

export interface OrderFillRow {
  key: string;
  executionKind: OrderFillExecutionKind;
  typeLabel: string;
  timestampLabel: string;
  quantityLabel: string;
  priceLabel: string;
  sideLabel: string;
  sideColor: OrdersWidgetBadgeColor;
  symbolLabel: string;
  cumulativeQuantityLabel: string;
  remainingQuantityLabel: string;
}

export const getOrderFillsPage = (
  fills: OrderFill[] | undefined,
): {
  fills: OrderFill[];
  nextToken?: string;
} => {
  if (!fills) {
    return { fills: EMPTY_FILLS };
  }

  const pageFills = fills.slice(0, ORDER_FILLS_PAGE_SIZE);
  const cursorCandidate = pageFills.at(-1)?.id;
  return {
    fills: pageFills,
    nextToken:
      fills.length > ORDER_FILLS_PAGE_SIZE &&
      typeof cursorCandidate === "string" &&
      cursorCandidate
        ? cursorCandidate
        : undefined,
  };
};

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

export const getOrderFillExecutionKind = (
  type: unknown,
): OrderFillExecutionKind => {
  const normalized = nonEmptyString(type)?.toLowerCase();
  if (normalized === "partial_fill") {
    return "partial_fill";
  }
  if (normalized === "fill") {
    return "fill";
  }
  return "unknown";
};

export const formatOrderFillType = (type: unknown): string => {
  const kind = getOrderFillExecutionKind(type);
  if (kind === "partial_fill") {
    return "Partial fill";
  }
  if (kind === "fill") {
    return "Final fill";
  }
  return nonEmptyString(type)
    ? formatOrderLabel(type)
    : ORDERS_UNAVAILABLE_VALUE;
};

export const toOrderFillRow = (
  fill: OrderFill,
  index: number,
): OrderFillRow => {
  const id = nonEmptyString(fill.id);
  const side = nonEmptyString(fill.side);

  return {
    key: id ?? `fill-${index}`,
    executionKind: getOrderFillExecutionKind(fill.type),
    typeLabel: formatOrderFillType(fill.type),
    timestampLabel: formatOrderTimestamp(fill.transaction_time),
    quantityLabel: formatOrderQuantity(fill.qty),
    priceLabel: formatOrderPrice(fill.price),
    sideLabel: side ? formatOrderLabel(side) : ORDERS_UNAVAILABLE_VALUE,
    sideColor: getOrderSideBadgeColor(fill.side),
    symbolLabel: nonEmptyString(fill.symbol) ?? ORDERS_UNAVAILABLE_VALUE,
    cumulativeQuantityLabel: formatOrderQuantity(fill.cum_qty),
    remainingQuantityLabel: formatOrderQuantity(fill.leaves_qty),
  };
};
